import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { painelService, PainelData } from "../../../services/painelService";
import { useAuth } from "../../../context/AuthContext";
import {
  canPriorizarMontagemUser,
  canVerTodasLojasUser,
} from "../../../lib/screenAccess";
import { useFornoEvents } from "../../../hooks/useFornoEvents";
import { useEntregaPainelEvents } from "../../../hooks/useEntregaPainelEvents";
import { useLojas } from "../../../hooks/useLojas";
import type { Loja } from "../../../types/loja";
import {
  filaPizzasService,
  prioridadeMontagemKey,
  type PrioridadeMontagemAlvo,
} from "../../../services/filaPizzasService";
import { subscribePainelRefresh } from "../../../utils/painelRefreshBroadcast";
import { useQueryClient } from "@tanstack/react-query";
import { OPERATIONAL_QUERY_OPTIONS } from "../../../constants/operationalQueryOptions";

interface PainelContextType {
  data: PainelData | null;
  loading: boolean;
  refreshing: boolean;
  isOffline: boolean;
  fetchPainel: () => Promise<void>;
  lojas: Loja[];
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
  showLojaSelector: boolean;
  canPriorizarMontagem: boolean;
  /** Chaves de `prioridadeMontagemKey`. */
  prioridadesMontagem: string[];
  priorizandoPrioridadeKey: string | null;
  priorizarMontagem: (alvo: PrioridadeMontagemAlvo) => Promise<void>;
  removendoPrioridadeKey: string | null;
  removerPrioridadeMontagem: (alvo: PrioridadeMontagemAlvo) => Promise<void>;
}

const PainelContext = createContext<PainelContextType | undefined>(undefined);

export const PainelProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const canPriorizarMontagem = canPriorizarMontagemUser(user);

  const { data: lojasData } = useLojas();
  const lojas = useMemo(() => lojasData ?? [], [lojasData]);

  useEffect(() => {
    queryClient.setQueryDefaults(["lojas"], OPERATIONAL_QUERY_OPTIONS);
    return () => {
      queryClient.setQueryDefaults(["lojas"], { refetchOnWindowFocus: true });
    };
  }, [queryClient]);

  const [data, setData] = useState<PainelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const [selectedLojaId, setSelectedLojaIdState] = useState<number | undefined>(
    undefined
  );

  const hasLoadedRef = useRef(false);
  const painelFetchInFlightRef = useRef(false);
  const painelFetchQueuedRef = useRef(false);
  const painelRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const fetchPainelRef = useRef<() => Promise<void>>(async () => {});
  const [fornoSseAvailable, setFornoSseAvailable] = useState(true);
  const [entregaSseAvailable, setEntregaSseAvailable] = useState(true);
  const sseAvailable = fornoSseAvailable && entregaSseAvailable;

  const effectiveLojaId = useMemo(() => {
    if (!podeVerTodasLojas) return user?.lojaId;
    return selectedLojaId ?? user?.lojaId;
  }, [podeVerTodasLojas, selectedLojaId, user?.lojaId]);

  const showLojaSelector = podeVerTodasLojas;

  const [prioridadesMontagem, setPrioridadesMontagem] = useState<string[]>([]);
  const [priorizandoPrioridadeKey, setPriorizandoPrioridadeKey] = useState<
    string | null
  >(null);
  const [removendoPrioridadeKey, setRemovendoPrioridadeKey] = useState<
    string | null
  >(null);
  const hasGoneOfflineRef = useRef(isOffline);

  const setSelectedLojaId = useCallback(
    (id: number | undefined) => {
      if (!podeVerTodasLojas) return;
      setSelectedLojaIdState(id);
    },
    [podeVerTodasLojas]
  );

  const fetchPainel = useCallback(async () => {
    if (painelFetchInFlightRef.current) {
      painelFetchQueuedRef.current = true;
      return;
    }
    painelFetchInFlightRef.current = true;
    const isFirstLoad = !hasLoadedRef.current;
    if (isFirstLoad) setLoading(true);
    else setRefreshing(true);
    try {
      const result = await painelService.getPainel(effectiveLojaId);
      setData(result);

      if (canPriorizarMontagem && effectiveLojaId != null) {
        try {
          const prioridades = await filaPizzasService.getPrioridadesMontagem({
            lojaId: effectiveLojaId,
          });
          setPrioridadesMontagem(prioridades);
        } catch {
          setPrioridadesMontagem([]);
        }
      } else {
        setPrioridadesMontagem([]);
      }
    } catch {
      setData({
        aguardandoMontagem: [],
        emMontagem: [],
        emForno: [],
        aguardandoEntrega: [],
        emEntrega: [],
        takeoutsProntos: [],
        finalizados: [],
        paozinhosAguardandoBordeiro: [],
        aguardandoBorda: [],
        temposMedios: {
          aguardandoMontagemSegundos: null,
          emMontagemSegundos: null,
          emFornoSegundos: null,
          aguardandoEntregaSegundos: null,
          emEntregaSegundosPorEndereco: null,
        },
      });
    } finally {
      painelFetchInFlightRef.current = false;
      if (isFirstLoad) setLoading(false);
      setRefreshing(false);
      hasLoadedRef.current = true;
      if (painelFetchQueuedRef.current) {
        painelFetchQueuedRef.current = false;
        queueMicrotask(() => {
          void fetchPainelRef.current();
        });
      }
    }
  }, [effectiveLojaId, canPriorizarMontagem]);

  fetchPainelRef.current = fetchPainel;

  const schedulePainelRefresh = useCallback(() => {
    if (painelRefreshDebounceRef.current != null) {
      clearTimeout(painelRefreshDebounceRef.current);
    }
    painelRefreshDebounceRef.current = setTimeout(() => {
      painelRefreshDebounceRef.current = null;
      void fetchPainelRef.current();
    }, 350);
  }, []);

  const onRankingStreamEvent = useCallback(() => {
    schedulePainelRefresh();
  }, [schedulePainelRefresh]);

  useFornoEvents(onRankingStreamEvent, effectiveLojaId, {
    onAvailabilityChange: setFornoSseAvailable,
  });
  useEntregaPainelEvents(onRankingStreamEvent, effectiveLojaId, {
    onAvailabilityChange: setEntregaSseAvailable,
  });

  useEffect(() => {
    return () => {
      if (painelRefreshDebounceRef.current != null) {
        clearTimeout(painelRefreshDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sseAvailable) return;

    void fetchPainelRef.current();
    const interval = setInterval(() => {
      void fetchPainelRef.current();
    }, 60_000);
    return () => clearInterval(interval);
  }, [sseAvailable]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void fetchPainelRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    return subscribePainelRefresh(() => {
      void fetchPainelRef.current();
    });
  }, []);

  useEffect(() => {
    if (selectedLojaId === undefined && user?.lojaId != null) {
      setSelectedLojaIdState(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  useEffect(() => {
    fetchPainel();
  }, [fetchPainel]);

  useEffect(() => {
    const handleOffline = () => {
      hasGoneOfflineRef.current = true;
      setIsOffline(true);
    };
    const handleOnline = () => {
      setIsOffline(false);
      if (hasGoneOfflineRef.current) {
        window.location.reload();
      } else {
        void fetchPainelRef.current();
      }
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // Atualização otimista: marca/desmarca a prioridade localmente na hora do
  // clique e sincroniza o painel em background. Evita esperar o refetch do
  // painel inteiro (lento com fila cheia) para o botão responder.
  const priorizarMontagem = useCallback(
    async (alvo: PrioridadeMontagemAlvo) => {
      const key = prioridadeMontagemKey(alvo);
      setPriorizandoPrioridadeKey(key);
      setPrioridadesMontagem((prev) =>
        prev.includes(key) ? prev : [key, ...prev]
      );
      try {
        await filaPizzasService.priorizarMontagem(alvo);
        schedulePainelRefresh();
      } catch (e) {
        setPrioridadesMontagem((prev) => prev.filter((k) => k !== key));
        throw e;
      } finally {
        setPriorizandoPrioridadeKey(null);
      }
    },
    [schedulePainelRefresh]
  );

  const removerPrioridadeMontagem = useCallback(
    async (alvo: PrioridadeMontagemAlvo) => {
      const key = prioridadeMontagemKey(alvo);
      setRemovendoPrioridadeKey(key);
      const havia = prioridadesMontagem.includes(key);
      setPrioridadesMontagem((prev) => prev.filter((k) => k !== key));
      try {
        await filaPizzasService.removerPrioridadeMontagem(alvo);
        schedulePainelRefresh();
      } catch (e) {
        if (havia) {
          setPrioridadesMontagem((prev) =>
            prev.includes(key) ? prev : [key, ...prev]
          );
        }
        throw e;
      } finally {
        setRemovendoPrioridadeKey(null);
      }
    },
    [schedulePainelRefresh, prioridadesMontagem]
  );

  const contextValue = useMemo(
    () => ({
      data,
      loading,
      refreshing,
      isOffline,
      fetchPainel,
      lojas,
      selectedLojaId: effectiveLojaId,
      setSelectedLojaId,
      showLojaSelector,
      canPriorizarMontagem,
      prioridadesMontagem,
      priorizandoPrioridadeKey,
      priorizarMontagem,
      removendoPrioridadeKey,
      removerPrioridadeMontagem,
    }),
    [
      data,
      loading,
      refreshing,
      isOffline,
      fetchPainel,
      lojas,
      effectiveLojaId,
      setSelectedLojaId,
      showLojaSelector,
      canPriorizarMontagem,
      prioridadesMontagem,
      priorizandoPrioridadeKey,
      priorizarMontagem,
      removendoPrioridadeKey,
      removerPrioridadeMontagem,
    ]
  );

  return (
    <PainelContext.Provider value={contextValue}>
      {children}
    </PainelContext.Provider>
  );
};

export const usePainel = () => {
  const context = useContext(PainelContext);
  if (!context) {
    throw new Error("usePainel must be used within PainelProvider");
  }
  return context;
};
